type StrapiVersion = 4 | 5 | "unknown";

const isObject = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isV4Entity = (value: unknown): boolean =>
  isObject(value) && isObject(value.attributes);

const isV5Entity = (value: unknown): boolean =>
  isObject(value) && typeof value.documentId === "string" && !isV4Entity(value);

const getSampleEntity = (payload: any) => {
  const data = payload?.data;
  return Array.isArray(data) ? data[0] : data;
};

const detectStrapiVersion = (payload: any): StrapiVersion => {
  const entity = getSampleEntity(payload);
  if (isV4Entity(entity)) return 4;
  if (isV5Entity(entity)) return 5;
  return "unknown";
};

const normalizeField = (value: any): any => {
  if (Array.isArray(value)) {
    const values = value.map(normalizeEntityOrComponent);
    return value.some(isV5Entity) ? { data: values } : values;
  }

  if (isV5Entity(value)) {
    return { data: normalizeEntity(value) };
  }

  if (!isObject(value)) return value;

  // A v4 relation/media wrapper may occur in an otherwise custom response.
  if (Object.prototype.hasOwnProperty.call(value, "data")) {
    return {
      ...value,
      data: Array.isArray(value.data)
        ? value.data.map(normalizeEntityOrComponent)
        : normalizeEntityOrComponent(value.data),
    };
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [key, normalizeField(nested)]),
  );
};

const normalizeEntity = (entity: Record<string, any>) => {
  if (isV4Entity(entity)) return entity;

  const { id, documentId, ...attributes } = entity;
  return {
    id,
    ...(documentId ? { documentId } : {}),
    attributes: Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [key, normalizeField(value)]),
    ),
  };
};

const normalizeEntityOrComponent = (value: any): any => {
  if (isV5Entity(value)) return normalizeEntity(value);
  if (Array.isArray(value)) return value.map(normalizeEntityOrComponent);
  if (!isObject(value)) return value;
  if (isV4Entity(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [key, normalizeField(nested)]),
  );
};

const normalizeStrapiResponse = (payload: any) => {
  if (detectStrapiVersion(payload) !== 5) return payload;

  return {
    ...payload,
    data: Array.isArray(payload.data)
      ? payload.data.map(normalizeEntity)
      : normalizeEntity(payload.data),
  };
};

const strapiFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response: any = await fetch(input, init);
  const payload = await response.json();

  response.strapiVersion = detectStrapiVersion(payload);
  response.data = normalizeStrapiResponse(payload);
  return response;
};

const strapiFetchWithFallback = async (
  primary: RequestInfo | URL,
  fallback: RequestInfo | URL,
  init?: RequestInit,
) => {
  const response = await strapiFetch(primary, init);

  if (response.status !== 404 && response.status !== 405) return response;
  return strapiFetch(fallback, init);
};

export {
  detectStrapiVersion,
  normalizeStrapiResponse,
  strapiFetch,
  strapiFetchWithFallback,
  StrapiVersion,
};
